import { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useAppContext } from "../../providers/ProviderAppContainer";
import TabHeader from "../common/TabHeader";
import FileInput from "../common/FileInput";
import { TEXT_NORMAL } from "../../style";

export default function DialogTaskComment({ visible, closeDialog, taskId, comment, onSaved }) {
    const isEdit = Boolean(comment?.id);
    const { requestAPI, showToast } = useAppContext();
    const [draft, setDraft] = useState({ comment: "", attachment: null });
    const [loading, setLoading] = useState();

    useEffect(() => {
        if (!visible) return;
        setDraft({
            comment: comment?.comment || "",
            attachment: comment?.attachment || null,
        });
    }, [comment, visible]);

    const save = useCallback(() => {
        if (isEdit) {
            requestAPI({
                requestPath: `user-tasks/comments/${comment.id}`,
                requestMethod: "PATCH",
                requestPostBody: {
                    comment: draft.comment,
                    attachment: draft.attachment || null,
                },
                setLoading,
                onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Comment", life: 2000 }),
                onResponseReceieved: (updated, responseCode) => {
                    if (updated && responseCode === 200) {
                        showToast({ severity: "success", summary: "Updated", detail: "Comment Updated", life: 1000 });
                        onSaved?.(updated, "edit");
                        closeDialog();
                    } else {
                        showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Comment", life: 2000 });
                    }
                },
            });
            return;
        }

        requestAPI({
            requestPath: `user-tasks/${taskId}/comments`,
            requestMethod: "POST",
            requestPostBody: {
                comment: draft.comment,
                attachment: draft.attachment || null,
            },
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Comment", life: 2000 }),
            onResponseReceieved: (created, responseCode) => {
                if (created && responseCode === 201) {
                    showToast({ severity: "success", summary: "Added", detail: "Comment Added", life: 1000 });
                    onSaved?.(created, "add");
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: created?.error || "Failed To Add Comment", life: 2000 });
                }
            },
        });
    }, [closeDialog, comment?.id, draft, isEdit, onSaved, requestAPI, showToast, taskId]);

    return (
        <Dialog
            pt={{ content: { className: "overflow-y-auto" } }}
            header={isEdit ? "Edit Comment" : "Add Comment"}
            visible={visible}
            className="w-11"
            onHide={closeDialog}
        >
            <TabHeader
                className="pt-3"
                title={isEdit ? "Edit Comment" : "Add Comment"}
                highlights={[isEdit ? "Update your comment" : "Leave a comment on this task"]}
            />

            <FloatLabel className="mt-5">
                <InputTextarea
                    className="w-full"
                    rows={4}
                    value={draft.comment || ""}
                    onChange={(e) => setDraft((prev) => ({ ...prev, comment: e.target.value }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label>Comment</label>
            </FloatLabel>

            <div className="mt-5">
                <FileInput
                    label="Attachment"
                    type="image"
                    cdn_url={draft.attachment}
                    setCDNUrl={(attachment) => setDraft((prev) => ({ ...prev, attachment }))}
                    disabled={loading}
                />
            </div>

            <Button
                className="mt-3"
                label={isEdit ? "Save Comment" : "Add Comment"}
                severity="warning"
                loading={loading}
                disabled={!draft.comment?.trim()}
                onClick={save}
            />
        </Dialog>
    );
}
